/**
 * <VideoWitness /> — 20-second live webcam recorder for the Refund
 * Compliance Desk. Uses the browser's MediaRecorder API (no external
 * dep). Emits the recorded blob upward via ``onRecorded(blob)``.
 *
 * Elite obsidian / gold styling to match the Vinci PDFs.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { Video, StopCircle, Play, RefreshCw, CheckCircle2 } from "lucide-react";

const MAX_SECONDS = 25; // hard cap; UI encourages ~20s

export function VideoWitness({ script, onRecorded, existingUrl, disabled }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const [state, setState] = useState("idle"); // idle | ready | recording | done | error
  const [secs, setSecs] = useState(0);
  const [blobUrl, setBlobUrl] = useState(existingUrl || null);
  const [err, setErr] = useState("");

  const cleanupStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  useEffect(() => () => cleanupStream(), [cleanupStream]);

  const arm = async () => {
    setErr("");
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = s;
      if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.muted = true; videoRef.current.play(); }
      setState("ready");
    } catch (e) {
      console.error(e);
      setErr("Could not access camera / microphone. Please grant permission and retry.");
      setState("error");
    }
  };

  const start = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const options = { mimeType: MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus" : "video/webm" };
    const rec = new MediaRecorder(streamRef.current, options);
    rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
      setState("done");
      cleanupStream();
      onRecorded && onRecorded(blob);
    };
    recRef.current = rec;
    rec.start();
    setState("recording");
    setSecs(0);
    timerRef.current = setInterval(() => {
      setSecs((s) => {
        if (s + 1 >= MAX_SECONDS) { stop(); return s + 1; }
        return s + 1;
      });
    }, 1000);
  };

  const stop = () => {
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const reset = () => {
    setBlobUrl(null); setState("idle"); setSecs(0);
    cleanupStream();
    onRecorded && onRecorded(null);
  };

  return (
    <div data-testid="video-witness" className="rounded-sm border border-[#C9A961]/25 bg-gradient-to-br from-[#0f0d0a] via-[#0b0908] to-[#0f0d0a] p-6">
      <div className="flex items-center gap-3 mb-4">
        <Video size={18} className="text-[#C9A961]" />
        <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C9A961]">Identity Witness — 20 s</div>
      </div>
      <div className="rounded-sm border border-[#C9A961]/15 bg-black/60 p-4 mb-4">
        <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#8A8A82] mb-2">
          Read this statement aloud, on camera, holding your original photo ID
        </div>
        <div data-testid="witness-script" className="font-serif text-[#F5F5F0] italic text-sm leading-relaxed">
          &ldquo;{script}&rdquo;
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 items-start">
        <div className="rounded-sm border border-[#C9A961]/20 bg-black overflow-hidden aspect-video relative">
          {blobUrl ? (
            <video src={blobUrl} controls className="w-full h-full object-cover" data-testid="witness-playback" />
          ) : (
            <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
          )}
          {state === "recording" && (
            <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/70 px-3 py-1.5 rounded-full">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-white tabular-nums">REC · {secs}s</span>
            </div>
          )}
        </div>
        <div className="space-y-3">
          {err && <div className="text-[13px] text-[#C0392B] font-body">{err}</div>}
          {state === "idle" && (
            <button data-testid="witness-arm" disabled={disabled}
              onClick={arm}
              className="w-full rounded-sm border border-[#C9A961]/50 bg-[#C9A961]/10 hover:bg-[#C9A961]/20 px-5 py-3 font-grotesk text-xs uppercase tracking-[0.28em] text-[#C9A961] transition-colors disabled:opacity-40">
              <Play size={14} className="inline mr-2 -mt-0.5" />Enable Camera & Microphone
            </button>
          )}
          {state === "ready" && (
            <button data-testid="witness-start" onClick={start}
              className="w-full rounded-sm bg-[#C9A961] hover:bg-[#F5D67A] text-black px-5 py-3 font-grotesk text-xs uppercase tracking-[0.28em] transition-colors">
              <span className="w-2 h-2 rounded-full bg-red-600 inline-block mr-2 align-middle" />
              Begin Recording ({MAX_SECONDS}s max)
            </button>
          )}
          {state === "recording" && (
            <button data-testid="witness-stop" onClick={stop}
              className="w-full rounded-sm bg-[#C0392B] hover:bg-red-700 text-white px-5 py-3 font-grotesk text-xs uppercase tracking-[0.28em] transition-colors">
              <StopCircle size={14} className="inline mr-2 -mt-0.5" />Stop & Save
            </button>
          )}
          {state === "done" && (
            <>
              <div className="flex items-center gap-2 text-[#2FB999] font-body text-sm">
                <CheckCircle2 size={16} /> Video captured — {secs}s. Preview above.
              </div>
              <button data-testid="witness-retake" onClick={reset}
                className="w-full rounded-sm border border-[#C9A961]/40 hover:border-[#C9A961]/60 text-[#C9A961] px-5 py-2.5 font-grotesk text-xs uppercase tracking-[0.28em]">
                <RefreshCw size={13} className="inline mr-2 -mt-0.5" />Re-record
              </button>
            </>
          )}
          <p className="font-body text-[11px] text-[#8A8A82] leading-relaxed">
            The recording is stored on Vinci's private object storage and can only be
            reviewed by the Compliance Desk. It becomes a permanent forensic artefact
            of your submission.
          </p>
        </div>
      </div>
    </div>
  );
}
